import {inject, Injectable, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable, firstValueFrom} from 'rxjs';
import {environment} from '../../../environments/environment';
import {AuthService} from './auth.service';
import Role from "../../shared/enums/role.enum";
import {Helper} from "../../shared/helper/helper";


@Injectable({
    providedIn: 'root'
})
export class SubscriptionService implements OnInit {

    private helper = inject(Helper);


    constructor(private http: HttpClient, private authService: AuthService) {
    }


    ngOnInit() {
        this.checkSubscriptionStatus();
    }

    /**
     * BehaviorSubject für den Abonnement-Status.
     * Speichert, ob der aktuelle Benutzer ein aktives Abonnement hat.
     * @private
     */
    private hasSubscriptionSubject = new BehaviorSubject<boolean>(false);

    /**
     * Observable für den Abonnement-Status.
     * Kann von Komponenten abonniert werden, um über Änderungen des Abonnement-Status informiert zu werden.
     */
    public hasSubscription$ = this.hasSubscriptionSubject.asObservable();

    /**
     * BehaviorSubject für die Sichtbarkeit des Abonnement-Overlays.
     * Steuert, ob das Overlay für Abonnement-Optionen angezeigt wird.
     * @private
     */
    private overlayVisibleSubject = new BehaviorSubject<boolean>(false);

    /**
     * Observable für die Sichtbarkeit des Abonnement-Overlays.
     * Kann von Komponenten abonniert werden, um über Änderungen der Overlay-Sichtbarkeit informiert zu werden.
     */
    public overlayVisible$ = this.overlayVisibleSubject.asObservable();


    // alter Code
    // ****************************
    // constructor(private http: HttpClient) {
    //     // Beim Initialisieren den Status prüfen
    //     this.checkSubscriptionStatus();
    //
    //     // Optional: Laden aus dem localStorage für die Entwicklung
    //     const savedStatus = localStorage.getItem('dev_hasSubscription');
    //     if (savedStatus !== null) {
    //         this.hasSubscriptionSubject.next(JSON.parse(savedStatus));
    //     }
    // }


    /**
     * Getter für den aktuellen Abonnement-Status.
     * Ermöglicht synchronen Zugriff auf den aktuellen Wert.
     *
     * @return {boolean} Der aktuelle Abonnement-Status (true, wenn ein aktives Abo vorhanden ist).
     */
    get hasSubscription(): boolean {
        return this.hasSubscriptionSubject.value;
    }

    /**
     * Setzt den Abonnement-Status auf den angegebenen Wert.
     * Aktualisiert den BehaviorSubject und speichert den Status optional im localStorage.
     *
     * @param {boolean} value - Der neue Abonnement-Status.
     */
    setHasSubscription(value: boolean): void {
        this.hasSubscriptionSubject.next(value);
    }


    /**
     * Prüft den Abonnement-Status basierend auf aktiven Verträgen.
     * Aktualisiert den hasAbo-Status entsprechend.
     * Prüfung im Backend, ob übergeordneter Account aktive Verträge hat
     *
     *
     *
     * @return {Promise<void>} Ein Promise, das nach der Prüfung aufgelöst wird.
     */
    async checkSubscriptionStatus(): Promise<void> {
        try {
            if (this.authService?.userValue) {

                if (this.helper.hasRole(Role.ADMINISTRATOR)) {
                    this.setHasSubscription(true);
                    return;
                }
                const hasActiveContracts = await this.hasActiveContracts();
                this.setHasSubscription(hasActiveContracts);
                return;
            } else {
                console.info('User nicht eingeloggt - Subscription-Check übersprungen');
                this.setHasSubscription(false);
                return;
            }

        } catch (error) {
            console.info('Fehler beim Prüfen des Abonnement-Status:', error);
            // Bei Fehler standardmäßig false setzen
            this.setHasSubscription(false);
        }
    }


    /**
     * Zeigt das Abonnement-Overlay an.
     * Setzt den Sichtbarkeitsstatus des Overlays auf true.
     */
    showSubscriptionOverlay(): void {
        this.overlayVisibleSubject.next(true);
    }

    /**
     * Verbirgt das Abonnement-Overlay.
     * Setzt den Sichtbarkeitsstatus des Overlays auf false.
     */
    hideSubscriptionOverlay(): void {
        this.overlayVisibleSubject.next(false);
    }


    /**
     * Ruft eine Liste von Subscription-Produkten basierend auf der angegebenen Kategorie und dem Typ ab.
     *
     * @param {number} category - Die Kategorie-ID der gewünschten Subscription-Produkte.
     * @return {Observable<any>} Ein Observable, das die abgerufenen Subscription-Produkte enthält.
     */
    getSubscriptionProducts(category: number): Observable<any> {
        return this.http.get<any>(`${environment.appApi}/fakturia/packages/${category}`);
    }

    /**
     * Ruft die Verträge eines Benutzers ab.
     *
     * @return {Observable<any>} Ein Observable, das die Daten der Benutzerverträge liefert.
     */
    getUserContracts(): Observable<any> {
        return this.http.get<any>(`${environment.appApi}/user/contracts`);
    }

    // alter Code
    // ****************************
    // async hasActiveContracts(): Promise<boolean> {
    //     const contracts = await firstValueFrom(this.getUserContracts());
    //     return contracts.length > 0;
    // }

    async hasActiveContracts(): Promise<boolean> {
        try {
            // Zusätzliche Prüfung vor API-Call
            if (!this.authService?.userValue) {
                return false;
            }

            const free = this.helper.isFakturiaFree()

            const contracts = await firstValueFrom(this.getUserContracts());


            if (this.helper.hasRole(Role.ADMINISTRATOR) || free) {
                return true;
            }

            return contracts && Array.isArray(contracts) && contracts.length > 0;

        } catch (error) {
            console.error('Fehler beim Abrufen der Verträge:', error);
            return false;
        }
    }

    /**
     * Ruft die maximale Anzahl an verfügbaren locations vom Server ab.
     *
     * @return {Observable<any>} Ein Observable, das die maximale Anzahl an locations als Antwort vom Server zurückgibt.
     */
    getMaxLocations(): Observable<any> {
        return this.http.get<any>(`${environment.appApi}/fakturia/max-locations`);
    }


    /**
     * Beendet einen bestehenden Vertrag basierend auf der angegebenen contractId.
     *
     * @param {number} contractId Die eindeutige ID des Vertrags, der gekündigt werden soll.
     * @return {Observable<any>} Gibt ein Observable zurück, das die Antwort des Servers nach der Kündigung des Vertrags enthält.
     */
    terminateContract(contractId: number): Observable<any> {
        return this.http.post<any>(`${environment.appApi}/user/contracts/${contractId}/cancel`, {});
    }
}
